/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { kanbify } from '../lib/kanbifyClient'

const AuthContext = createContext({})

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null)
    const [profile, setProfile] = useState(null)
    const [loading, setLoading] = useState(true)
    const [sessionExpired, setSessionExpired] = useState(false)
    const [isSuperAdmin, setIsSuperAdmin] = useState(false)
    const previousUserRef = useRef(null)
    const lastProfileSig = useRef(null)
    // True only while an explicit user-initiated logout is in flight, so
    // the onAuthStateChange SIGNED_OUT below is NOT misreported as an
    // involuntary "session expired" (the shim fires SIGNED_OUT for both).
    const intentionalSignOutRef = useRef(false)

    const fetchProfile = useCallback(async (userId) => {
        const { data, error } = await kanbify
            .from('profiles')
            .select('*, user_badges ( badge_key, awarded_at )')
            .eq('id', userId)
            .single()
        // Resilience (Teo 2026-06-04): a transient profile fetch failure (deploy
        // restart, network blip, 15s poll race) must NOT drop us to the "Niciun
        // cont legat de o companie" screen while the session is still valid.
        // On error OR empty result, keep the last good profile and retry on the
        // next poll. profile is only cleared on an explicit SIGNED_OUT (below).
        if (error || !data) { setLoading(false); return }
        // Realtime is emulated by a 15s poll (shim). This callback fires
        // every 15s; calling setProfile with a fresh object each time
        // churned the global AuthContext value → app-wide re-render +
        // scroll-jump-to-top every 15s ("se dă refresh la 15s, sare
        // sus"). Only update state when the profile ACTUALLY changed so
        // the reference stays stable between identical polls.
        const sig = JSON.stringify(data ?? null)
        if (sig !== lastProfileSig.current) {
            lastProfileSig.current = sig
            setProfile(data)
        }
        setLoading(false)
    }, [])

    useEffect(() => {
        kanbify.auth.getSession().then(({ data: { session } }) => {
            const currentUser = session?.user ?? null
            setUser(currentUser)
            previousUserRef.current = currentUser
            if (currentUser) fetchProfile(currentUser.id)
            else setLoading(false)
        })

        const { data: { subscription } } = kanbify.auth.onAuthStateChange((event, session) => {
            const wasLoggedIn = previousUserRef.current !== null
            const newUser = session?.user ?? null

            setUser(newUser)

            if (newUser) {
                previousUserRef.current = newUser
                fetchProfile(newUser.id)
            } else {
                setProfile(null)
                setIsSuperAdmin(false)
                setLoading(false)

                // Session expired or token refresh failed while user was
                // logged in — but NOT when the user clicked "Log out"
                // themselves (intentional logout must be silent).
                const intentional = intentionalSignOutRef.current
                intentionalSignOutRef.current = false
                previousUserRef.current = null
                if (wasLoggedIn && !intentional && (event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED')) {
                    setSessionExpired(true)
                }
            }
        })

        return () => subscription.unsubscribe()
    }, [fetchProfile])

    const dismissSessionExpired = useCallback(() => setSessionExpired(false), [])

    // Super-admin is platform-scoped (SUPER_ADMIN_EMAILS, server-resolved
    // via the is_super_admin RPC) — NOT a profiles.role. Only this signal
    // gates platform actions like creating/joining companies. Reset on
    // logout is handled where setProfile(null) runs (no stale leak).
    useEffect(() => {
        if (!user?.id) return
        let cancelled = false
        kanbify.rpc('is_super_admin')
            .then(({ data }) => { if (!cancelled) setIsSuperAdmin(data === true || data === 1) })
            .catch(() => { /* non-fatal: default stays false (least privilege) */ })
        return () => { cancelled = true }
    }, [user?.id])

    // Realtime profile sync (points, level, streak update live)
    useEffect(() => {
        if (!user) return
        const channel = kanbify.channel(`profile:${user.id}`)
            .on('postgres_changes', {
                event: 'UPDATE', schema: 'public', table: 'profiles',
                filter: `id=eq.${user.id}`
            }, () => { fetchProfile(user.id) })
            .subscribe()
        return () => kanbify.removeChannel(channel)
    }, [fetchProfile, user])

    async function signUp(email, password, fullName, role = 'employee', inviteToken = null) {
        const { data, error } = await kanbify.auth.signUp({
            email,
            password,
            options: {
                data: { full_name: fullName, role, invite_token: inviteToken },
            },
        })
        if (error) throw error
        return data
    }

    // Per-company self-register link (?join=TOKEN). Throws the same
    // { message, code } shape as signUp so AuthPage pivots to Login on
    // account_exists / join_link_invalid.
    async function signUpViaCompanyLink(email, fullName, joinToken) {
        const { data, error } = await kanbify.auth.signUpViaCompanyLink({
            email, fullName, joinToken,
        })
        if (error) throw error
        return data // { pending:true, email } — NOT a session
    }

    // Confirm a ?confirm_signup= link with the password the user just set
    // → creates the real account + logs in.
    async function confirmSignup(token, password) {
        const { data, error } = await kanbify.auth.confirmSignup(token, password)
        if (error) throw error
        return data
    }

    async function signIn(email, password, remember = true) {
        const { data, error } = await kanbify.auth.signInWithPassword({ email, password, remember })
        if (error) throw error
        return data
    }

    async function signOut() {
        // Mark this as a deliberate logout so onAuthStateChange does not
        // raise the "Sesiunea a expirat" toast for a SIGNED_OUT the user
        // asked for.
        intentionalSignOutRef.current = true
        await kanbify.auth.signOut()
        setUser(null)
        setProfile(null)
        setIsSuperAdmin(false)
        // Drop a stale ?invite= / ?reset_token= left over from an earlier
        // invite/recovery link so AuthPage reopens on Log in (its sign-up
        // tab defaults open when ?invite= is present) instead of dumping
        // the just-logged-out user on the registration form.
        try {
            const u = new URL(window.location.href)
            if (u.searchParams.has('invite') || u.searchParams.has('reset_token')) {
                u.searchParams.delete('invite')
                u.searchParams.delete('reset_token')
                window.history.replaceState({}, '', u.pathname + u.search + u.hash)
            }
        } catch { /* non-fatal: URL cleanup is best-effort */ }
    }

    async function refreshProfile() {
        if (user) await fetchProfile(user.id)
    }

    const isAdmin = profile?.role === 'admin'
    const isManager = profile?.role === 'manager'
    const canCreate = Boolean(profile?.company_id) && (profile?.role === 'admin' || profile?.role === 'manager')

    return (
        <AuthContext.Provider value={{
            user, profile, loading, isAdmin, isManager, canCreate, isSuperAdmin,
            sessionExpired, dismissSessionExpired,
            signUp, signUpViaCompanyLink, confirmSignup, signIn, signOut, refreshProfile,
        }}>
            {children}
        </AuthContext.Provider>
    )
}

export function useAuth() {
    return useContext(AuthContext)
}
